import { TextInput, Pressable } from "react-native";
import { View, Text } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { useImpersonationSettings } from "@src/features/auth/hooks/useImpersonationSettings";
import { useSearchUsers } from "@visioni/a3s-access/app";
import { useCallback, useState } from "react";
import { useRouter } from "expo-router";
import { SettingsListItem } from "../web/SettingsListItem";

export const ImpersonationSetting = () => {
  const { theme } = useUnistyles();
  const router = useRouter();

  const [isOpen, setIsOpen] = useState(false);

  const { impersonatedUserId, impersonatedLabel, setImpersonation, clearImpersonation } = useImpersonationSettings();
  const {
    results: searchResults,
    isSearching,
    query: searchQuery,
    setQuery: setSearchQuery,
    clear: clearSearch,
  } = useSearchUsers();

  const handleSelectUser = useCallback(
    (selectedUser: { auth0Id: string; email: string; name?: string }) => {
      setImpersonation(selectedUser.auth0Id, selectedUser.name ?? selectedUser.email, selectedUser.email);
      clearSearch();
      router.replace("/");
    },
    [setImpersonation, clearSearch, router],
  );

  const handleClearImpersonation = useCallback(() => {
    clearImpersonation();
    clearSearch();
    router.replace("/");
  }, [clearImpersonation, clearSearch, router]);

  return impersonatedUserId ?
      <>
        <View
          style={{
            backgroundColor: theme.colors.surfaceCardInnerWarning,
            borderRadius: theme.spacing.md,
            padding: theme.spacing.lg,
          }}
        >
          <Text textStyle="xs/medium" color="text">
            Viewing as: {impersonatedLabel}
          </Text>
        </View>
        <SettingsListItem title="Clear Impersonation" onPress={handleClearImpersonation} showChevron={false} />
      </>
    : <>
        <SettingsListItem
          title="Impersonate a User"
          onPress={() => setIsOpen(!isOpen)}
          showChevron={true}
          chevronIcon={isOpen ? "chevronDown" : "chevronRight"}
        />
        {isOpen && (
          <>
            <TextInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="Search by name or email (min 3 chars)"
              autoCapitalize="none"
              autoCorrect={false}
              style={{
                borderWidth: 1,
                borderColor: theme.colors.borderCard,
                borderRadius: theme.spacing.md,
                paddingVertical: theme.spacing.lg,
                paddingHorizontal: theme.spacing.xl,
                color: theme.colors.text,
                backgroundColor: theme.colors.surfaceCard,
                fontSize: 14,
              }}
              placeholderTextColor={theme.colors.textMuted}
            />
            {isSearching && (
              <Text textStyle="xs/regular" color="textMuted">
                Searching…
              </Text>
            )}
            {searchResults.length > 0 && (
              <View
                style={{
                  borderWidth: 1,
                  borderColor: theme.colors.borderCard,
                  borderRadius: theme.spacing.md,
                  overflow: "hidden",
                }}
              >
                {searchResults.map((result, index) => (
                  <Pressable
                    key={result.auth0Id}
                    onPress={() => handleSelectUser(result)}
                    style={{
                      paddingVertical: theme.spacing.lg,
                      paddingHorizontal: theme.spacing.xl,
                      borderBottomWidth: index < searchResults.length - 1 ? 1 : 0,
                      borderBottomColor: theme.colors.borderCard,
                    }}
                  >
                    <Text textStyle="sm/medium">{result.name ?? result.email}</Text>
                    <Text textStyle="xs/regular" color="textMuted">
                      {result.email}
                      {result.legacyId ? ` (legacy: ${result.legacyId})` : ""}
                    </Text>
                  </Pressable>
                ))}
              </View>
            )}
          </>
        )}
      </>;
};
